import React, { useMemo } from "react";
// import IdeasContext from "../../../IdeasContext"; // Removed - using props instead
import { formatBigMonetaryValueIdeas } from "../../../ideas-table-helpers/Helpers";
import { Flex, Text, Skeleton, Box } from "@mantine/core";
import { updateAccessorByState } from "../../../../../utils";
import Bar from "./Bar";
import { MAIN_DARK_PRIMARY } from "../../../../../constants/colors";

// Type definitions for component props
interface BarCellProps {
  data: Record<string, any>;
  accessor: string;
  sortState?: string; // Added as prop
  renderParams?: {
    prefix?: string;
    default?: string;
    multiplyByComponentWeight?: boolean;
    backgroundColor?: string;
    topValues?: any;
  };
}

// Main component
export const BarCell: React.FC<BarCellProps> = ({
  data,
  accessor,
  sortState = "total", // Default value
  renderParams = {},
}) => {
  
  const multiplyByComponentWeight =
    renderParams?.multiplyByComponentWeight || false;

  const topVal = renderParams?.topValues ? renderParams.topValues[sortState] : 0

  // Helper function to safely access nested values
  const getNestedValue = (obj: Record<string, any>, path: string): any => {
    return path
      .split(".")
      .reduce(
        (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
        obj
      );
  };

  // Memoize expensive calculations
  const calculatedValues = useMemo(() => {
    // Update accessors based on the current sort state
    const totalAccessor = updateAccessorByState(accessor, "total");
    const assignedAccessor = updateAccessorByState(accessor, "assigned");
    const suggestedAccessor = updateAccessorByState(accessor, "suggested");

    // Retrieve values from the data object
    const totalValue = getNestedValue(data, totalAccessor);
    const assignedValue = getNestedValue(data, assignedAccessor);
    const suggestedValue = getNestedValue(data, suggestedAccessor);

    // Retrieve component weight, defaulting to 1 if not present
    const componentWeight = data.componentObj?.weight || 1;

    // Calculate multiplied values if required
    const multipliedTotalValue = multiplyByComponentWeight
      ? totalValue * componentWeight
      : totalValue;
    const multipliedAssignedValue = multiplyByComponentWeight
      ? assignedValue * componentWeight
      : assignedValue;
    const multipliedSuggestedValue = multiplyByComponentWeight
      ? suggestedValue * componentWeight
      : suggestedValue;

    const relevantValue =
      sortState === "suggested"
        ? multipliedSuggestedValue
        : sortState === "assigned"
          ? multipliedAssignedValue
          : sortState === "total"
            ? multipliedTotalValue
            : multipliedTotalValue;

    const multipliedTopValue = multiplyByComponentWeight
      ? topVal * componentWeight
      : topVal;
    const percVal = (topVal === 0) ? 0 : (relevantValue / multipliedTopValue) * 100;

    return {
      multipliedTotalValue,
      multipliedAssignedValue,
      multipliedSuggestedValue,
      percVal
    };
  }, [data, accessor, sortState, multiplyByComponentWeight, topVal]);

  // Memoize display content
  const displayContent = useMemo(() => {
    switch (sortState) {
    case "total":
      return (
        <Flex>
          <Text fz={12} weight={600} color={MAIN_DARK_PRIMARY} style={{ textAlign: 'left' }}>
            {formatBigMonetaryValueIdeas(
              calculatedValues.multipliedTotalValue || 0,
              renderParams.prefix
            )}
          </Text>
        </Flex>
      );

    case "assigned":
      return (
        <Text fz={12} weight={600} color={MAIN_DARK_PRIMARY} style={{ textAlign: 'left' }}>
          {formatBigMonetaryValueIdeas(
            calculatedValues.multipliedAssignedValue || 0,
            renderParams.prefix
          )}
        </Text>
      );

    case "suggested":
      return (
        <Text fz={12} weight={600} color={MAIN_DARK_PRIMARY} style={{ textAlign: 'left' }}>
          {formatBigMonetaryValueIdeas(
            calculatedValues.multipliedSuggestedValue || 0,
            renderParams.prefix
          )}
        </Text>
      );

    default:
      return <Text>{renderParams.default || "-"}</Text>;
    }
  }, [sortState, calculatedValues, renderParams.prefix, renderParams.default]);

  return (
    <Bar displayContent={displayContent} barWidthPercantage={calculatedValues.percVal || 0} />
  );
};

// Skeleton component for BarCell
export const BarCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  const maxWidth = col.maxWidth;
  
  // Bar cells typically show monetary values, adjust width accordingly
  const valueWidth = columnWidth ? Math.min(parseInt(columnWidth) * 0.8, 100) : "70%";
  const barWidth = columnWidth ? Math.min(parseInt(columnWidth) * 0.5, 80) : "40%";
  
  return (
    <Box 
      sx={{ 
        position: 'relative', 
        height: '100%', 
        minHeight: 32,
        width: columnWidth || "100%",
        minWidth,
        maxWidth
      }}
    >
      <Flex align="center" h="100%">
        <Skeleton height={26} width={valueWidth} radius="sm" />
      </Flex>
      {/* Background bar skeleton */}
      {/* <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: '#f1f3f4',
          borderRadius: 2,
        }}
      >
        <Skeleton height={4} width={barWidth} radius={2} />
      </Box> */}
    </Box>
  );
};
